import { ReactNode } from "react";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import AnimatedNumber from "./AnimatedNumber";
import Sparkline from "./Sparkline";

interface RichStatCardProps {
  label: string;
  value: number;
  icon?: ReactNode;
  data?: number[];
  delta?: { value: number; label?: string } | null;
  tone?: "brand" | "emerald" | "violet" | "amber";
  format?: (n: number) => string;
}

const toneMap: Record<
  string,
  { sparkStroke: string; iconBg: string; iconText: string; halo: string }
> = {
  brand: {
    sparkStroke: "#6366f1",
    iconBg: "bg-brand-50",
    iconText: "text-brand-600",
    halo: "from-brand-200/40 to-transparent",
  },
  emerald: {
    sparkStroke: "#059669",
    iconBg: "bg-emerald-50",
    iconText: "text-emerald-600",
    halo: "from-emerald-200/40 to-transparent",
  },
  violet: {
    sparkStroke: "#7c3aed",
    iconBg: "bg-violet-50",
    iconText: "text-violet-600",
    halo: "from-violet-200/40 to-transparent",
  },
  amber: {
    sparkStroke: "#d97706",
    iconBg: "bg-amber-50",
    iconText: "text-amber-600",
    halo: "from-amber-200/40 to-transparent",
  },
};

const RichStatCard = ({
  label,
  value,
  icon,
  data = [],
  delta,
  tone = "brand",
  format,
}: RichStatCardProps) => {
  const t = toneMap[tone];
  const isPositive = (delta?.value ?? 0) >= 0;

  return (
    <div className="group relative bg-white border border-ink-200/70 rounded-2xl shadow-card hover:shadow-card-hover transition-all overflow-hidden">
      <div
        className={`absolute -top-20 -right-20 w-44 h-44 rounded-full bg-gradient-to-br ${t.halo} blur-2xl`}
      />

      <div className="relative p-5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">
            {label}
          </div>
          <div className="mt-1.5 font-display text-stat-xl text-ink-900 tabular-nums leading-none">
            <AnimatedNumber value={value} format={format} />
          </div>
          {delta && (
            <div className="mt-2 flex items-center gap-1.5 text-xs">
              <span
                className={`inline-flex items-center gap-0.5 px-1.5 h-5 rounded-md font-medium ${
                  isPositive
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-rose-50 text-rose-700"
                }`}
              >
                {isPositive ? (
                  <ArrowUpRight size={11} />
                ) : (
                  <ArrowDownRight size={11} />
                )}
                {Math.abs(delta.value)}%
              </span>
              {delta.label && (
                <span className="text-ink-500">{delta.label}</span>
              )}
            </div>
          )}
        </div>

        {icon && (
          <span
            className={`shrink-0 w-9 h-9 rounded-xl ${t.iconBg} ${t.iconText} flex items-center justify-center`}
          >
            {icon}
          </span>
        )}
      </div>

      <div className="relative px-2 pb-2">
        <Sparkline
          data={data}
          width={300}
          height={48}
          stroke={t.sparkStroke}
          className="w-full"
        />
      </div>
    </div>
  );
};

export default RichStatCard;
